import Modal from 'flowbite/lib/esm/components/modal/index.js';
import Swiper from 'swiper';
import { Navigation, Keyboard, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const GLOBAL_MODAL_ID = 'global-iframe-modal';
const FLOWBITE_OPTIONS = {
  placement: 'center',
  backdrop: 'dynamic',
  backdropClasses: 'bg-overlay fixed inset-0 z-40',
  closable: true,
};

function getGlobalModal() {
  return document.getElementById(GLOBAL_MODAL_ID);
}

function patchBackdrop(modal) {
  if (modal && modal.backdropEl) {
    modal.backdropEl.className = 'bg-overlay fixed inset-0 z-40';
  }
}

function focusModalCloseButton(modalEl) {
  if (!modalEl) return;
  const closeButton = modalEl.querySelector('[data-focus-first], [data-modal-hide]');
  if (closeButton && typeof closeButton.focus === 'function') {
    closeButton.focus({ preventScroll: true });
  }
}

function ensureGlobalModalInstance(modalEl) {
  if (modalEl.__modalInstance) return modalEl.__modalInstance;
  const modal = new Modal(modalEl, FLOWBITE_OPTIONS, { id: GLOBAL_MODAL_ID, override: true });
  modalEl.__modalInstance = modal;
  return modal;
}

function findRichCaption(figure, galleryRoot) {
  if (!figure) return null;

  const isCaptionContainer = (node) => {
    if (!node?.classList) return false;
    return node.classList.contains('gallery-subtext-content') || node.classList.contains('slide__text');
  };

  let current = figure;
  while (current && current !== galleryRoot) {
    const parent = current.parentElement;
    if (!parent) break;

    const directCaption = Array.from(parent.children).find((child) => {
      if (child === current) return false;
      if (isCaptionContainer(child)) return true;
      return !!child.querySelector?.('.gallery-subtext-content, .slide__text');
    });

    if (directCaption) {
      if (isCaptionContainer(directCaption)) return directCaption;
      return directCaption.querySelector('.gallery-subtext-content, .slide__text');
    }

    current = parent;
  }

  return figure.querySelector('.gallery-subtext-content, .slide__text');
}

function getGalleryItems(galleryRoot) {
  return Array.from(galleryRoot.querySelectorAll('.gallery__items .gallery__item'))
    .map((figure) => {
      const img = figure.querySelector('img');
      const richCaption = findRichCaption(figure, galleryRoot);
      if (!img || !img.getAttribute('src')) return null;
      return {
        src: figure.getAttribute('data-gallery-src') || img.getAttribute('src') || '',
        srcset: figure.getAttribute('data-gallery-srcset') || img.getAttribute('srcset') || '',
        sizes: figure.getAttribute('data-gallery-sizes') || img.getAttribute('sizes') || '',
        alt: img.getAttribute('alt') || '',
        width: img.getAttribute('width') || '',
        height: img.getAttribute('height') || '',
        description: (figure.querySelector('figcaption')?.textContent || '').trim(),
        descriptionHtml: (richCaption?.innerHTML || '').trim(),
      };
    })
    .filter(Boolean);
}

function renderGalleryShell(host) {
  host.innerHTML = `
    <div data-modal-gallery-wrap>
      <div class="relative w-full px-0 md:px-24 2xl:px-32">
        <div class="mx-auto w-full max-w-content">
          <h2 id="gallery-title" class="sr-only">Bildergalerie</h2>
          <div class="swiper gallery-swiper" aria-labelledby="gallery-title" aria-live="polite">
            <div class="swiper-wrapper" data-gallery-swiper-wrapper></div>
            <button type="button" class="swiper-button-prev" aria-label="Vorheriges Bild">
              <svg class="swiper__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <use href="/wp-content/themes/carthago/assets/sprite.svg#arrow-right" xlink:href="/wp-content/themes/carthago/assets/sprite.svg#arrow-right"></use>
              </svg>
            </button>
            <button type="button" class="swiper-button-next" aria-label="Nächstes Bild">
              <svg class="swiper__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <use href="/wp-content/themes/carthago/assets/sprite.svg#arrow-right" xlink:href="/wp-content/themes/carthago/assets/sprite.svg#arrow-right"></use>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="sr-only" data-gallery-announce aria-live="polite" aria-atomic="true">
        <span data-prefix>Bild</span>
        <span data-current></span>
        <span data-of>von</span>
        <span data-total></span>
      </div>
    </div>
  `;
}

function clearRuntimeIframes(container) {
  if (!container) return;
  container.querySelectorAll('iframe').forEach((iframe) => {
    if (iframe.getAttribute('src')) {
      iframe.setAttribute('src', 'about:blank');
      iframe.setAttribute('src', '');
    }
  });
}

function buildSlides(modalEl, items) {
  const wrapper = modalEl.querySelector('[data-gallery-swiper-wrapper]');
  if (!wrapper) return;
  wrapper.innerHTML = '';

  const fragment = document.createDocumentFragment();
  items.forEach((item, index) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const figure = document.createElement('figure');
    figure.className = 'gallery__item gallery__item--modal-popup max-w-1200 mx-auto px-0 md:px-8';

    const img = document.createElement('img');
    img.className = 'gallery__img';
    img.setAttribute('src', item.src);
    img.setAttribute('alt', item.alt || `Bild ${index + 1}`);
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    if (item.srcset) img.setAttribute('srcset', item.srcset);
    if (item.sizes) img.setAttribute('sizes', item.sizes);
    if (item.width) img.setAttribute('width', item.width);
    if (item.height) img.setAttribute('height', item.height);

    const caption = document.createElement('figcaption');
    if (item.descriptionHtml) {
      caption.innerHTML = item.descriptionHtml;
    } else {
      caption.textContent = item.description;
    }

    figure.appendChild(img);
    figure.appendChild(caption);
    slide.appendChild(figure);
    fragment.appendChild(slide);
  });

  wrapper.appendChild(fragment);
}

function updateAnnounce(modalEl, index, total) {
  const announce = modalEl.querySelector('[data-gallery-announce]');
  if (!announce) return;
  const current = announce.querySelector('[data-current]');
  const full = announce.querySelector('[data-total]');
  if (current) current.textContent = String(index + 1);
  if (full) full.textContent = String(total);
}

export function initGalleryModal() {
  const galleries = Array.from(document.querySelectorAll('.gallery'));

  const modalEl = getGlobalModal();
  if (!modalEl) return;

  const modal = ensureGlobalModalInstance(modalEl);
  const mediaHost = modalEl.querySelector('[data-modal-media-host]');
  if (!mediaHost) return;

  let swiperInstance = null;

  const cleanupGallery = () => {
    if (swiperInstance) {
      swiperInstance.destroy(true, true);
      swiperInstance = null;
    }
    clearRuntimeIframes(mediaHost);
    mediaHost.innerHTML = '';
    mediaHost.classList.add('hidden');
    modalEl.dataset.modalContent = '';
    modalEl.setAttribute('aria-label', 'Medieninhalte');
  };
  modalEl.__cleanupGalleryContent = cleanupGallery;

  if (modalEl.dataset.galleryCleanupBound !== '1') {
    modalEl.addEventListener('hide.tw.modal', cleanupGallery);
    modalEl.addEventListener('hidden.tw.modal', cleanupGallery);
    modalEl.dataset.galleryCleanupBound = '1';
  }

  const openGalleryAt = (galleryRoot, slideIndex) => {
    const items = getGalleryItems(galleryRoot);
    if (!items.length) return;
    const safeIndex = Math.min(Math.max(0, slideIndex), items.length - 1);

    if (typeof modalEl.__cleanupIframeContent === 'function') {
      modalEl.__cleanupIframeContent();
    } else {
      clearRuntimeIframes(mediaHost);
      mediaHost.innerHTML = '';
      mediaHost.classList.add('hidden');
    }

    mediaHost.classList.remove('hidden');
    modalEl.dataset.modalContent = 'gallery';
    modalEl.setAttribute('aria-label', 'Bildergalerie');

    renderGalleryShell(mediaHost);
    buildSlides(modalEl, items);

    const swiperEl = modalEl.querySelector('.gallery-swiper');
    if (!swiperEl) return;

    swiperInstance = new Swiper(swiperEl, {
      modules: [Navigation, Keyboard, A11y],
      slidesPerView: 1,
      spaceBetween: 16,
      loop: false,
      navigation: {
        prevEl: modalEl.querySelector('.swiper-button-prev'),
        nextEl: modalEl.querySelector('.swiper-button-next'),
      },
      keyboard: { enabled: true, onlyInViewport: false },
      a11y: { enabled: true },
    });

    swiperInstance.on('slideChange', () => {
      updateAnnounce(modalEl, swiperInstance.activeIndex, items.length);
    });

    modal._options.backdropClasses = FLOWBITE_OPTIONS.backdropClasses;
    modal._options.backdrop = FLOWBITE_OPTIONS.backdrop;
    modal.show();
    patchBackdrop(modal);
    requestAnimationFrame(() => focusModalCloseButton(modalEl));
    swiperInstance.slideTo(safeIndex, 0);
    updateAnnounce(modalEl, safeIndex, items.length);
  };

  galleries.forEach((galleryRoot) => {
    const previewFigures = Array.from(
      galleryRoot.querySelectorAll('.gallery__items .gallery__item:not(.hidden)')
    );

    previewFigures.forEach((figure) => {
      figure.setAttribute('role', 'button');
      figure.setAttribute('tabindex', '0');
      const imgAlt = figure.querySelector('img')?.getAttribute('alt') || '';
      figure.setAttribute('aria-label', imgAlt ? `Bild öffnen: ${imgAlt}` : 'Bild öffnen');

      const openFromFigure = () => {
        const validFigures = Array.from(
          galleryRoot.querySelectorAll('.gallery__items .gallery__item')
        ).filter((node) => !!node.querySelector('img[src]'));
        const targetIndex = Math.max(0, validFigures.indexOf(figure));
        openGalleryAt(galleryRoot, targetIndex);
      };

      figure.addEventListener('click', openFromFigure);
      figure.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openFromFigure();
      });
    });
  });

  if (modalEl.dataset.galleryTriggerBound !== '1') {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gallery-open]');
      if (!trigger) return;

      const targetSelector = trigger.dataset.galleryOpen || '';
      if (!targetSelector) return;

      const galleryRoot = document.querySelector(targetSelector);
      if (!galleryRoot) return;

      event.preventDefault();
      const nextIndex = Number.parseInt(trigger.dataset.galleryIndex || '0', 10);
      openGalleryAt(galleryRoot, Number.isFinite(nextIndex) ? nextIndex : 0);
    });

    modalEl.dataset.galleryTriggerBound = '1';
  }

  if (modalEl.dataset.galleryCloseBound !== '1') {
    modalEl.querySelectorAll('[data-modal-hide]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof modalEl.__cleanupIframeContent === 'function') {
          modalEl.__cleanupIframeContent();
        }
        if (typeof modalEl.__cleanupGalleryContent === 'function') {
          modalEl.__cleanupGalleryContent();
        }
        const instance = ensureGlobalModalInstance(modalEl);
        instance.hide();
      });
    });
    modalEl.dataset.galleryCloseBound = '1';
  }
}
